import bbox from '@turf/bbox';
import type { MultiPolygon, Polygon, Position } from 'geojson';
import { format2 } from 'utils/number';
import type {
  AreaEditCompareRow,
  AreaEditCompareSection,
} from 'types/area-workspace';

const EMPTY = '—';

const FIELD_LABELS: Record<string, string> = {
  geojson: 'Boundary',
  gross_acres: 'Gross Acres',
  name: 'Name',
  display_name: 'Display Name',
  notes: 'Notes',
  display_color: 'Colour',
};

/**
 * A field the label map does not name, as words rather than `some_field`.
 *
 * @param {string} field - The attribute name.
 * @return {string} The label.
 */
const humanize = (field: string): string =>
  field
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export type AreaShape = Polygon | MultiPolygon;

/**
 * The value as a boundary, when it is one.
 *
 * @param {unknown} value - One side of a row.
 * @return {AreaShape | null} The polygon, or null.
 */
export const asShape = (value: unknown): AreaShape | null => {
  const shape = value as { type?: string; coordinates?: unknown } | null;
  return shape &&
    (shape.type === 'Polygon' || shape.type === 'MultiPolygon') &&
    Array.isArray(shape.coordinates)
    ? (shape as AreaShape)
    : null;
};

/**
 * One side of a text row as it reads: acreage as the detail card shows it, a blank
 * as a dash, everything else as itself.
 *
 * @param {string} field - The row's field.
 * @param {unknown} value - One side of the row.
 * @return {string} The text.
 */
export const displayValue = (field: string, value: unknown): string => {
  if (value === null || value === undefined || value === '') return EMPTY;
  if (field === 'gross_acres') return `${format2(value as number)} ac`;
  if (typeof value === 'string') return value;
  return typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : JSON.stringify(value);
};

/** One row of the Compare table. */
export interface AreaCompareTableRow {
  id: string;
  field: string;
  label: string;
  from: unknown;
  to: unknown;
  status: AreaEditCompareRow['status'];
}

export interface AreaCompareTableSection {
  key: string;
  label: string;
  rows: AreaCompareTableRow[];
}

/**
 * The endpoint's sections as the sheet draws them: labelled rows, in the order
 * served, with sections that changed nothing left out.
 *
 * @param {AreaEditCompareSection[]} sections - The compare endpoint's sections.
 * @return {AreaCompareTableSection[]} Sections carrying at least one row.
 */
export const compareSections = (
  sections: AreaEditCompareSection[]
): AreaCompareTableSection[] =>
  sections
    .map((section) => ({
      key: section.key,
      label: section.label,
      rows: section.rows.map((row) => ({
        id: `${section.key}-${row.field}`,
        field: row.field,
        label: FIELD_LABELS[row.field] ?? humanize(row.field),
        from: row.from,
        to: row.to,
        status: row.status,
      })),
    }))
    .filter((section) => section.rows.length > 0);

/**
 * The rings of a boundary, whatever its type.
 *
 * @param {AreaShape} shape - The boundary.
 * @return {Position[][]} Its rings.
 */
const ringsOf = (shape: AreaShape): Position[][] =>
  shape.type === 'Polygon' ? shape.coordinates : shape.coordinates.flat();

/** A frame two boundaries share, so each thumbnail is drawn at the same scale. */
export interface ShapeFrame {
  minX: number;
  minY: number;
  width: number;
  height: number;
  /** Longitude is shrunk by this so a square section reads square. */
  scaleX: number;
}

/**
 * The frame that holds every given boundary.
 *
 * @param {AreaShape[]} shapes - The boundaries to fit.
 * @return {ShapeFrame | null} The frame, or null when there is nothing to draw.
 */
export const shapeFrame = (shapes: AreaShape[]): ShapeFrame | null => {
  if (!shapes.some((shape) => ringsOf(shape).some((ring) => ring.length > 0))) {
    return null;
  }
  // Turf boxes the points in a loop: `Math.min(...)` over every vertex overflows
  // the call stack on a boundary with hundreds of thousands of them.
  const [west, south, east, north] = bbox({
    type: 'GeometryCollection',
    geometries: shapes,
  });
  const scaleX = Math.cos((((south + north) / 2) * Math.PI) / 180);
  const minX = west * scaleX;
  const minY = -north;
  return {
    minX,
    minY,
    // A degenerate extent still needs a non-zero box to draw into.
    width: east * scaleX - minX || 1e-6,
    height: north - south || 1e-6,
    scaleX,
  };
};

/**
 * A boundary as an SVG path in the frame's coordinates, north up.
 *
 * @param {AreaShape} shape - The boundary.
 * @param {ShapeFrame} frame - The shared frame.
 * @return {string} The path's `d`.
 */
export const shapePath = (shape: AreaShape, frame: ShapeFrame): string =>
  ringsOf(shape)
    .map(
      (ring) =>
        `M${ring
          .map(
            ([lng, lat]) =>
              `${lng * frame.scaleX - frame.minX},${-lat - frame.minY}`
          )
          .join('L')}Z`
    )
    .join('');
